// Zoom
export const MIN_ZOOM = 0.5
export const MAX_ZOOM = 2.0
export const ZOOM_SPEED = 0.1

// Activity defaults
export const DEFAULT_ACTIVITY_WIDTH = 100
export const DEFAULT_ACTIVITY_HEIGHT = 50

// Undo/redo
export const MAX_UNDO = 50

// Minimap
export const MINIMAP_WIDTH = 200
export const MINIMAP_HEIGHT = 150

// Hit-testing
export const TRANSITION_HIT_DISTANCE = 10
export const EDGE_DRAG_THRESHOLD = 12

// Long-press / double-tap
export const LONG_PRESS_DURATION = 500
export const DOUBLE_TAP_DELAY = 300

// Colors per activity type
export const ACTIVITY_COLORS: Record<string, string> = {
  start: '#4CAF50',
  end: '#f44336',
  activity: '#2196F3',
}

// Darker border colors (same hue, lower brightness)
export const ACTIVITY_STROKE_COLORS: Record<string, string> = {
  start: '#2E7D32',
  end: '#C62828',
  activity: '#1565C0',
}

// Selection highlight
export const SELECTION_COLOR = '#ffeb3b'

// Properties panel
export const PANEL_WIDTH = 300

// Default tools configuration (matches original workflow designer)
import type { ToolsConfig } from './types'

export const DEFAULT_TOOLS_CONFIG: ToolsConfig = {
  Tools: {
    Tool: [
      {
        Type: 'Application',
        Id: 'SignTrans',
        Name: 'Perform Approval',
        Icon: '\u2705',
        FormalParameters: {
          FormalParameter: [
            { Id: 'processInstanceID', Index: 1, Mode: 'IN', DefaultValue: 'process_id', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'roleCodeSign', Index: 2, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'entityCode', Index: 3, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'formatCode', Index: 4, Mode: 'IN', DefaultValue: 'Intimation-Email', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'xslFileName', Index: 5, Mode: 'IN', DefaultValue: 'obj_name.xsl', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'SignFor', Index: 6, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'context1', Index: 7, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'context2', Index: 8, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'context3', Index: 9, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
          ],
        },
      },
      {
        // The approval engine reads recipients/subject/body BY NAME
        // (approval_engine._named_params maps each ActualParameter → the
        // FormalParameter at the same sorted (index,id) position), so these
        // Ids must stay `to` / `subject` / `body`.
        Type: 'Application',
        Id: 'EMail',
        Name: 'EMail',
        Icon: '\u{1F4E7}',
        Description: 'Send email notification',
        FormalParameters: {
          FormalParameter: [
            // Recipients: comma list of `submitter`, `role:CODE`, `user:ID`,
            // a `{column}` placeholder, or a literal email address.
            { Id: 'to', Index: 1, Mode: 'IN', DefaultValue: 'submitter', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'subject', Index: 2, Mode: 'IN', DefaultValue: 'Approval notification', DataType: { BasicType: { Type: 'STRING' } } },
            { Id: 'body', Index: 3, Mode: 'IN', DefaultValue: 'Record {txn_id} needs your attention.', DataType: { BasicType: { Type: 'STRING' } } },
          ],
        },
      },
      {
        // The executor reads `sql` (falling back to `query`) and `connection`
        // by name from these FormalParameters.
        Type: 'Execute-SQL',
        Id: 'ExecuteQuery',
        Name: 'ExecuteQuery',
        Icon: '\u{1F5C3}',
        Description: 'Execute SQL query to update status',
        FormalParameters: {
          FormalParameter: [
            // SQL run on the project data DB. May reference `{column}`
            // placeholders, bound as parameters against the staged record.
            { Id: 'sql', Index: 1, Mode: 'IN', DefaultValue: "UPDATE my_table SET status = 'APPROVED' WHERE id = {id}", DataType: { BasicType: { Type: 'STRING' } } },
            // Optional named connection (blank = the bound project DB).
            { Id: 'connection', Index: 2, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
          ],
        },
      },
      {
        // Runs a transaction action with the staged record. The executor reads
        // `object` (blank = bound object), `action`, and `record_id` by name.
        Type: 'Application',
        Id: 'CallAction',
        Name: 'Call Action',
        Icon: '\u{2699}',
        Description: 'Run a transaction action with the staged values',
        FormalParameters: {
          FormalParameter: [
            // Target transaction object (blank = the bound object).
            { Id: 'object', Index: 1, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            // The transaction action name to run (e.g. approve / cancel).
            { Id: 'action', Index: 2, Mode: 'IN', DataType: { BasicType: { Type: 'STRING' } } },
            // Optional `{column}` placeholder for the target record id.
            { Id: 'record_id', Index: 3, Mode: 'IN', DefaultValue: '{id}', DataType: { BasicType: { Type: 'STRING' } } },
          ],
        },
      },
    ],
  },
}
